/**
 * Direct video (.mp4/.webm/.m3u8) on a room screen, kept in step with the
 * room's shared clock: { playing, t, at } from the sharer, applied against the
 * server clock so everyone sees the same frame (±0.75 s). No cloud browser and
 * no per-minute cost — each viewer streams the file themselves.
 */
import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { serverNow } from '../services/roomScreens';
import { needsResync, videoTimeAt, type VideoSyncState } from '../utils/roomScreenSync';

export function useSyncedVideo(url: string | null, state: VideoSyncState | null) {
  const [texture, setTexture] = useState<THREE.VideoTexture | null>(null);
  const [aspect, setAspect] = useState<number | null>(null);
  const [status, setStatus] = useState('');
  const [needsSound, setNeedsSound] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (!url) return;
    let disposed = false;
    let hls: { destroy(): void } | null = null;
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.playsInline = true;
    video.preload = 'auto';
    videoRef.current = video;
    (window as any).__roomScreenVideos = { ...((window as any).__roomScreenVideos || {}), [url]: video };
    setStatus('Loading video…');

    const isHls = /\.m3u8$/i.test(new URL(url).pathname);
    (async () => {
      if (isHls && !video.canPlayType('application/vnd.apple.mpegurl')) {
        const { default: Hls } = await import('hls.js');
        if (disposed) return;
        if (Hls.isSupported()) {
          const h = new Hls();
          h.loadSource(url);
          h.attachMedia(video);
          hls = h;
          return;
        }
      }
      video.src = url;
    })();

    const tex = new THREE.VideoTexture(video);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = false;
    const ready = () => {
      if (disposed || !video.videoWidth) return;
      setAspect(video.videoWidth / video.videoHeight);
      setTexture(tex);
      setStatus('');
    };
    const failed = () => { if (!disposed) setStatus('Unable to play this video'); };
    video.addEventListener('loadeddata', ready);
    video.addEventListener('error', failed);

    // Follow the shared clock twice a second (and right away on changes).
    const tick = () => {
      const s = stateRef.current;
      if (disposed || !s || video.readyState < 1) return;
      const target = videoTimeAt(s, serverNow(), video.duration);
      const drift = target - video.currentTime;
      if (!s.playing) {
        // Paused: everyone shows the exact same frame.
        if (Math.abs(drift) > 0.05) { try { video.currentTime = target; } catch { /* not seekable yet */ } }
        video.playbackRate = 1;
      } else if (needsResync(video.currentTime, target)) {
        try { video.currentTime = target; } catch { /* not seekable yet */ }
        video.playbackRate = 1;
      } else {
        // Small drift: catch up/slow down by up to 8% instead of a visible seek.
        video.playbackRate = Math.abs(drift) > 0.08 ? 1 + Math.max(-0.08, Math.min(0.08, drift * 0.4)) : 1;
      }
      if (s.playing && video.paused) {
        video.play().catch(() => {
          // Autoplay with sound needs a gesture: play muted until the viewer clicks.
          if (disposed) return;
          video.muted = true;
          setNeedsSound(true);
          void video.play().catch(() => {});
        });
      } else if (!s.playing && !video.paused) video.pause();
    };
    video.addEventListener('loadedmetadata', tick);
    const timer = setInterval(tick, 500);
    (video as any).__syncTick = tick;

    return () => {
      disposed = true;
      clearInterval(timer);
      hls?.destroy();
      video.pause();
      video.removeAttribute('src');
      video.load();
      tex.dispose();
      setTexture(null);
      setAspect(null);
      videoRef.current = null;
      const reg = (window as any).__roomScreenVideos;
      if (reg) delete reg[url];
    };
  }, [url]);

  // Apply play/pause/seek from the room immediately.
  useEffect(() => { (videoRef.current as any)?.__syncTick?.(); }, [state?.playing, state?.t, state?.at]);

  const enableSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    setNeedsSound(false);
    void v.play().catch(() => {});
  };

  return { texture, aspect, status, needsSound, enableSound };
}
